import {
  BrandAsset,
  Divider,
  NavigationItem,
  NavigationMenu,
  NavigationSection,
  NavigationSidebar,
  NavigationTitle,
  TopNavRegionToggle,
} from "@certn/cascadia-react";
import { RegionContext } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  checkArgumentsTemplatePermissions,
  useCanAccessSettings,
  useCanViewBranding,
  useCanViewGroups,
  userPagePermissions,
} from "../../components/permissions/Permissions";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { useEntitledRegions } from "../../hooks/useEntitledRegions";
import { useUser } from "../../hooks/useUser";

export function SettingsNavigation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    clientPortalCaseTags,
    clientPortalQuestionnaireTemplates,
    clientPortalUserManagement,
    employmentVerificationsOrderable,
    credentialVerificationsOrderable,
    clientPortalOrderNonBiometricIdCanada,
    mfaEnabled,
    enableDataRetentionSettings,
    enableCobrandingForApplications,
    enableAddressBasedInternationalCriminalRecordCheckOrdering,
    enableAddressBasedBankruptcyCheckOrdering,
    enableAddressBasedDirectorshipCheckOrdering,
    enableAddressBasedCreditCheckOrdering,
    enableCivilLitigationRecordCheck,
    enableInternationalCriminalDatabase,
    releaseCustomQuestionnaireCheck,
    releaseExperienceVerifications,
  } = useFlags();

  const { hasPermission } = usePermissions();
  const { details } = useUser();
  const { canSwitchRegion } = useEntitledRegions();
  const canViewGroups = useCanViewGroups();
  const canAccessSettings = useCanAccessSettings();
  const canViewBranding = useCanViewBranding();
  const region = useContext(RegionContext);
  const showDataRetention = enableDataRetentionSettings && region !== "au";

  const hasTenacyPermissablePurpose =
    details?.customer_groups?.some(
      (group) => group.permissible_purpose === "TENANCY"
    ) || false;
  const hasInvestingOrLendingPermissiblePurpose =
    details?.customer_groups?.some(
      (group) =>
        group.permissible_purpose === "INVESTING" ||
        group.permissible_purpose === "LENDING"
    ) || false;

  const canViewCustomQuestionnaireTemplates =
    releaseCustomQuestionnaireCheck &&
    hasPermission(checkArgumentsTemplatePermissions);

  const canViewExperienceVerificationTemplates =
    releaseExperienceVerifications &&
    hasPermission(checkArgumentsTemplatePermissions);

  return (
    <NavigationSidebar
      toolbar={
        <>
          <BrandAsset height="27" width="92" brand="certn" type="full" />
          {canSwitchRegion && <TopNavRegionToggle />}
        </>
      }
    >
      <NavigationSection>
        <NavigationItem
          key="back"
          icon="arrow_back"
          title={t("Back")}
          onPress={() => navigate("/")}
        />
      </NavigationSection>
      <NavigationTitle style="global/heading-large" isSection>
        {t("Settings")}
      </NavigationTitle>
      <NavigationMenu sectionTitle={t("ACCOUNT")}>
        {canViewGroups &&
          clientPortalUserManagement &&
          hasPermission(userPagePermissions) && (
            <NavigationItem
              key="users"
              title={t("Users")}
              isActive={location.pathname.endsWith(`/users`)}
              onPress={() => navigate(`/settings/users`)}
            />
          )}
        {clientPortalCaseTags &&
          hasPermission({
            action: "read",
            target: "tag",
            scope: "all",
          }) && (
            <NavigationItem
              title={t("Tags")}
              onPress={() => navigate("/settings/tags")}
              isActive={location.pathname.includes("/settings/tags")}
              data-testid="settings-navigation-button"
            />
          )}
        {enableCobrandingForApplications && canViewBranding && (
          <NavigationItem
            title={t("Branding")}
            onPress={() => navigate("/settings/branding")}
            isActive={location.pathname.includes("/settings/branding")}
            data-testid="settings-navigation-button"
          />
        )}
        {hasPermission({
          action: "configure",
          target: "public_api",
          scope: "all",
        }) && (
          <NavigationItem
            title={t("Integrations")}
            onPress={() => navigate("/settings/integrations")}
            isActive={location.pathname.includes("/settings/integrations")}
            data-testid="settings-integrations-navigation-button"
          />
        )}
        {(mfaEnabled || showDataRetention) && canAccessSettings && (
          <NavigationItem
            title={t("Security")}
            onPress={() => navigate("/settings/security")}
            isActive={location.pathname.includes("/settings/security")}
            data-testid="settings-security-button"
          />
        )}
        {clientPortalOrderNonBiometricIdCanada &&
          hasPermission({
            action: "read",
            target: "check_settings",
            scope: "all",
          }) && (
            <NavigationItem
              key="check-settings"
              title={t("Checks")}
              isActive={location.pathname.endsWith(`/check-settings`)}
              onPress={() => navigate(`/settings/check-settings`)}
            />
          )}
      </NavigationMenu>
      <NavigationSection>
        <Divider />
      </NavigationSection>
      <div data-testid="templates-section">
        <NavigationMenu sectionTitle={t("TEMPLATES")}>
          {enableAddressBasedBankruptcyCheckOrdering &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="bankruptcy-templates"
                title={t("Bankruptcy")}
                isActive={location.pathname.includes(`/bankruptcy-templates`)}
                onPress={() => navigate(`/settings/bankruptcy-templates`)}
              />
            )}
          {enableCivilLitigationRecordCheck &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="civil-litigation-templates"
                title={t("Civil litigation")}
                isActive={location.pathname.includes(
                  `/civil-litigation-templates`
                )}
                onPress={() => navigate(`/settings/civil-litigation-templates`)}
              />
            )}
          {credentialVerificationsOrderable &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="credential-verification-templates"
                title={t("Credential verification")}
                isActive={location.pathname.includes(
                  `/credential-verification-templates`
                )}
                onPress={() =>
                  navigate(`/settings/credential-verification-templates`)
                }
              />
            )}
          {enableAddressBasedCreditCheckOrdering &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="credit-report-templates"
                title={t("Credit report")}
                isActive={location.pathname.includes(
                  `/credit-report-templates`
                )}
                onPress={() => navigate(`/settings/credit-report-templates`)}
              />
            )}
          {enableAddressBasedInternationalCriminalRecordCheckOrdering &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="criminal-record-templates"
                title={t("Criminal record")}
                isActive={location.pathname.includes(
                  `/criminal-record-templates`
                )}
                onPress={() => navigate(`/settings/criminal-record-templates`)}
              />
            )}
          {canViewCustomQuestionnaireTemplates && (
            <NavigationItem
              key="custom-questionnaire"
              title={t("Custom questionnaire")}
              isActive={location.pathname.endsWith(
                `/custom-questionnaire-templates`
              )}
              onPress={() =>
                navigate(`/settings/custom-questionnaire-templates`)
              }
            />
          )}
          {enableAddressBasedDirectorshipCheckOrdering &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="directorship-templates"
                title={t("Directorship")}
                isActive={location.pathname.includes(`/directorship-templates`)}
                onPress={() => navigate(`/settings/directorship-templates`)}
              />
            )}
          {hasPermission(checkArgumentsTemplatePermissions) && (
            <NavigationItem
              key="education-verification-templates"
              title={t("Education verification")}
              isActive={location.pathname.includes(
                `/education-verification-templates`
              )}
              onPress={() =>
                navigate(`/settings/education-verification-templates`)
              }
            />
          )}
          {employmentVerificationsOrderable &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="employment-verification-templates"
                title={t("Employment verification")}
                isActive={location.pathname.endsWith(
                  `/employment-verification-templates`
                )}
                onPress={() =>
                  navigate(`/settings/employment-verification-templates`)
                }
              />
            )}
          {canViewExperienceVerificationTemplates && (
            <NavigationItem
              key="experience-verification"
              title={t("Experience verification")}
              isActive={location.pathname.endsWith(
                `/experience-verification-templates`
              )}
              onPress={() =>
                navigate(`/settings/experience-verification-templates`)
              }
            />
          )}
          {clientPortalQuestionnaireTemplates &&
            (hasTenacyPermissablePurpose ||
              hasInvestingOrLendingPermissiblePurpose) && (
              <NavigationItem
                key="fitness-propriety-templates"
                title={t("Fitness & propriety")}
                isActive={location.pathname.includes(
                  `/fitness-propriety-templates`
                )}
                onPress={() =>
                  navigate(`/settings/fitness-propriety-templates`)
                }
              />
            )}
          {enableInternationalCriminalDatabase &&
            hasPermission(checkArgumentsTemplatePermissions) && (
              <NavigationItem
                key="international-criminal-database-templates"
                title={t("International criminal database")}
                isActive={location.pathname.includes(
                  `/international-criminal-database-templates`
                )}
                onPress={() =>
                  navigate(
                    `/settings/international-criminal-database-templates`
                  )
                }
              />
            )}
          {clientPortalQuestionnaireTemplates &&
            hasTenacyPermissablePurpose &&
            hasPermission({
              action: "read",
              target: "questionnaire",
              scope: "all",
            }) && (
              <NavigationItem
                key="questionnaire"
                title={t("Questionnaire")}
                isActive={location.pathname.endsWith(
                  `/questionnaire-templates`
                )}
                onPress={() => navigate(`/settings/questionnaire-templates`)}
              />
            )}
          {/* TODO: gate behind caseManagementReferenceSuperunitFirstFulfillment once flag is client-side enabled */}
          {hasPermission(checkArgumentsTemplatePermissions) && (
            <NavigationItem
              key="reference-check-templates"
              title={t("Reference")}
              isActive={location.pathname.includes(
                `/reference-check-templates`
              )}
              onPress={() => navigate(`/settings/reference-check-templates`)}
            />
          )}
        </NavigationMenu>
      </div>
      <div style={{ minHeight: "var(--space-4)" }} />
    </NavigationSidebar>
  );
}
